// @flow
import _debug from 'debug';
import toggleArray from '@justpark/helpers/src/toggleArray/toggleArray';
import locationDetailReviews from '@justpark/api/src/requests/getLocationDetailReviews';
import walkingDistanceRequest from '@justpark/api/src/requests/getWalkingDistance';
import getRequestError from '@justpark/api/src/helpers/getRequestError';
import calculateDateDiff from '@justpark/helpers/src/calculateDateDiff/calculateDateDiff';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { makeActionCreator } from '../helpers/utils';

const debug = _debug('jp:reducers:search');

type LatLng = {
  lat: number,
  lng: number
};

export const distance = (
  { lat: lat1, lng: lng1 }: LatLng,
  { coordinates: { lat: lat2, lng: lng2 } }: { coordinates: LatLng }
) => {
  const deglen = 110.25;
  const x = lat1 - lat2;
  const y = (lng1 - lng2) * Math.cos(lat2);
  return deglen * Math.sqrt(x * x + y * y);
};

const MONTHLY_UPGRADE_MODAL_VISIBLE = 'MONTHLY_UPGRADE_MODAL_VISIBLE';
const MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE =
  'MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE';
const MODAL_DISMISSED = 'DISMISSED';

export type State = {|
  hoveredId: number,
  showFilterPanel: boolean,
  reviews: {
    [number]: Array<any>
  },
  filterCheckboxes: Array<string>,
  currentPanPosition: { lat: number, lng: number } | null,
  monthlyUpgradeModal: null | 'DISMISSED' | 'VISIBLE',
  dismissedOccupancySearchKey: boolean,
  walkingTimes: { [number]: number }
|};

export const defaultCoords = {
  lat: 51.5072178,
  lng: -0.1275862
};

export const initialState: () => State = () => ({
  selectedId: 0,
  hoveredId: 0,
  showFilterPanel: false,
  reviews: {},
  filterCheckboxes: [],
  currentPanPosition: null,
  monthlyUpgradeModal: null,
  dismissedOccupancySearchKey: false,
  walkingTimes: {}
});

const HOVER_ITEM_ENTER = 'search/HOVER_ITEM_ENTER';
const HOVER_ITEM_EXIT = 'search/HOVER_ITEM_EXIT';
export const TOGGLE_FILTER_PANEL = 'search/TOGGLE_FILTER_PANEL';

const PANNED = 'search/PANNED';
const SET_MONTHLY_UPGRADE_MESSAGE = 'search/SET_MONTHLY_UPGRADE_MESSAGE';
const DISMISS_MONTHLY_UPGRADE_MESSAGE =
  'search/DISMISS_MONTHLY_UPGRADE_MESSAGE';
export const TOGGLE_FILTER_CHECKBOX = 'search/TOGGLE_FILTER_CHECKBOX ';
export const CLEAR_FILTER_CHECKBOXES = 'search/CLEAR_FILTER_CHECKBOXES ';
export const SET_FILTER_CHECKBOXES = 'search/SET_FILTER_CHECKBOXES ';
export const DISMISS_OCCUPANCY_SEARCH_KEY =
  'search/DISMISS_OCCUPANCY_SEARCH_KEY';

type Action =
  | {| type: typeof HOVER_ITEM_ENTER, id: number |}
  | {| type: typeof DISMISS_MONTHLY_UPGRADE_MESSAGE |}
  | {| type: typeof TOGGLE_DATEPICKER |}
  | {| type: typeof TOGGLE_HOW_IT_WORKS |}
  | {| type: typeof TOGGLE_FILTER_CHECKBOX |}
  | {| type: typeof CLEAR_FILTER_CHECKBOXES |}
  | {| type: typeof SET_FILTER_CHECKBOXES |}
  | {| type: typeof TOGGLE_FILTER_PANEL |}
  | {| type: typeof PANNED, payload: { lat: number, lng: number } |}
  | {| type: typeof DISMISS_OCCUPANCY_SEARCH_KEY |};

export const splitCoords = (coords) =>
  coords.split(',').map((v) => parseFloat(v));

const getMonthlyUpgradeModalState = (state: State, dates) => {
  const { arriving, leaving } = dates;

  if (leaving === null) {
    return state.monthlyUpgradeModal;
  }

  const dayDiff = calculateDateDiff(arriving, leaving);

  if (dayDiff < 25 && state.monthlyUpgradeModal !== MODAL_DISMISSED) {
    return null;
  }

  if (
    dayDiff >= 25 &&
    dayDiff < 90 &&
    state.monthlyUpgradeModal !== MODAL_DISMISSED
  ) {
    return MONTHLY_UPGRADE_MODAL_VISIBLE;
  }

  if (dayDiff >= 90) {
    return MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE;
  }

  return state.monthlyUpgradeModal;
};

export const loadReviews = createAsyncThunk(
  'search/loadReviews',
  async (payload, { rejectWithValue, extra: { jpApiClient } }) => {
    try {
      const { id, show = true } = payload;

      const request = locationDetailReviews(id);
      const { data } = await jpApiClient(request);
      return {
        id,
        show,
        data: data.data
      };
    } catch (e) {
      return rejectWithValue(getRequestError(e));
    }
  }
);

export const loadWalkingDistance = createAsyncThunk(
  'search/loadWalkingDistance',
  async (
    { coords, listingIds },
    { rejectWithValue, extra: { jpApiClient } }
  ) => {
    try {
      const request = walkingDistanceRequest(coords, listingIds);
      const { data } = await jpApiClient(request);

      return data.data;
    } catch (e) {
      return rejectWithValue(getRequestError(e));
    }
  }
);

const sliceName = 'search';

const searchSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset: () => initialState()
  },
  extraReducers: {
    [PANNED](state, action) {
      debug(PANNED, action);
      const { payload } = action;
      return {
        ...state,
        currentPanPosition: {
          lat: payload.lat,
          lng: payload.lng
        }
      };
    },
    [HOVER_ITEM_ENTER](state, action) {
      debug(HOVER_ITEM_ENTER);
      const { id } = action;
      return {
        ...state,
        hoveredId: id
      };
    },
    [HOVER_ITEM_EXIT](state) {
      debug(HOVER_ITEM_EXIT);
      return {
        ...state,
        hoveredId: null
      };
    },
    [loadReviews.fulfilled](state, action) {
      debug(loadReviews.fulfilled);
      const { id, data } = action.payload;

      return {
        ...state,
        reviews: {
          ...state.reviews,
          [id]: data === null ? [] : data
        }
      };
    },
    [SET_MONTHLY_UPGRADE_MESSAGE](state, action) {
      debug(SET_MONTHLY_UPGRADE_MESSAGE);

      const { dates } = action;

      const monthlyUpgradeModal = getMonthlyUpgradeModalState(state, dates);

      return {
        ...state,
        monthlyUpgradeModal
      };
    },
    [DISMISS_MONTHLY_UPGRADE_MESSAGE](state) {
      return {
        ...state,
        monthlyUpgradeModal: MODAL_DISMISSED
      };
    },
    [loadWalkingDistance.fulfilled](state, action) {
      debug(loadWalkingDistance.fulfilled, action);

      if (action.payload) {
        const walkingTimes = {};
        action.payload.forEach((item) => {
          walkingTimes[item.listingId] = item.time;
        });

        return {
          ...state,
          walkingTimes
        };
      }

      return state;
    },
    [TOGGLE_FILTER_PANEL](state) {
      debug(TOGGLE_FILTER_PANEL);
      const { showFilterPanel, filterCheckboxes } = state;

      return {
        ...state,
        showFilterPanel: !showFilterPanel,
        filterCheckboxes: !showFilterPanel ? [] : filterCheckboxes
      };
    },
    [TOGGLE_FILTER_CHECKBOX](state, action) {
      debug(TOGGLE_FILTER_CHECKBOX, action);
      const value = action.payload;
      const filterCheckboxes = [...state.filterCheckboxes];
      const newFilters = toggleArray(filterCheckboxes, value);

      return {
        ...state,
        filterCheckboxes: newFilters
      };
    },
    [SET_FILTER_CHECKBOXES](state, action) {
      debug(SET_FILTER_CHECKBOXES, action);

      const filterCheckboxes = [...action.payload, ...state.filterCheckboxes];

      const noDuplicateFilters = filterCheckboxes.filter(
        (item, index) => filterCheckboxes.indexOf(item) === index
      );

      return {
        ...state,
        filterCheckboxes: noDuplicateFilters
      };
    },
    [CLEAR_FILTER_CHECKBOXES](state) {
      debug(CLEAR_FILTER_CHECKBOXES);

      return {
        ...state,
        filterCheckboxes: []
      };
    },
    [DISMISS_OCCUPANCY_SEARCH_KEY](state) {
      debug(DISMISS_OCCUPANCY_SEARCH_KEY);
      return {
        ...state,
        dismissedOccupancySearchKey: true
      };
    }
  }
});

export default searchSlice;

export const { reset } = searchSlice.actions;

export const setMonthlyUpgradeMessage = ({ dates }): Action => ({
  dates,
  type: SET_MONTHLY_UPGRADE_MESSAGE
});

export const dismissOccupancySearchKey = (): Action => ({
  type: DISMISS_OCCUPANCY_SEARCH_KEY
});

export const setPanned = (payload: { lat: number, lng: number }) => ({
  type: PANNED,
  payload
});

export const dismissMonthlyUpgradeMessage = (): Action => ({
  type: DISMISS_MONTHLY_UPGRADE_MESSAGE
});

export function onMouseOver(type: string, id: number) {
  return (dispatch: (*) => Promise<*>) => {
    dispatch({ type: HOVER_ITEM_ENTER, id });
  };
}

export function onMouseOut() {
  return (dispatch: (*) => Promise<*>) => {
    dispatch({ type: HOVER_ITEM_EXIT });
  };
}

export const toggleFilterPanel = makeActionCreator(TOGGLE_FILTER_PANEL);
export const toggleFilterCheckbox = makeActionCreator(
  TOGGLE_FILTER_CHECKBOX,
  'payload'
);
export const setFilterCheckboxes = makeActionCreator(
  SET_FILTER_CHECKBOXES,
  'payload'
);
export const clearFilterCheckbox = makeActionCreator(CLEAR_FILTER_CHECKBOXES);

export const isMonthlyUpgradeModalVisible = (state: { search: State }) =>
  state.search.monthlyUpgradeModal === MONTHLY_UPGRADE_MODAL_VISIBLE ||
  state.search.monthlyUpgradeModal === MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE;

export const selectIsMonthlyAutoUpgraded = (state: { search: State }) =>
  state.search.monthlyUpgradeModal === MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE;

export const selectSearchHasDismissedOccupancyKey = (state) =>
  state.search.dismissedOccupancySearchKey;

export const selectSearchWalkingTimes = (state) => state.search?.walkingTimes;

export const selectSearchCurrentPanPosition = (state) =>
  state.search?.currentPanPosition;

export const selectSearchReviews = (state) => state.search.reviews;

export const selectSearchHoveredId = (state) => state.search.hoveredId;
